import type { Profile, Track } from "../core/types";
import { planQueries, type Query } from "../core/plan";
import { speedIsFilterable } from "../core/vocab";
import { cached } from "../lib/cache";
import { decodeEntities } from "../lib/text";

declare const __JAMENDO_CLIENT_ID__: string;

/**
 * client_id берётся из /config.json, который контейнер пишет при старте.
 * Так один и тот же образ работает с любым ключом, и сборка от него не зависит.
 * Для `vite dev` подставляется значение из .env на этапе сборки.
 */
let clientId: string | null = null;

async function getClientId(): Promise<string> {
  if (clientId !== null) return clientId;
  try {
    const r = await fetch("/config.json", { cache: "no-store" });
    if (r.ok) {
      const c = await r.json();
      if (c.jamendoClientId) return (clientId = c.jamendoClientId);
    }
  } catch { /* локальная разработка — конфига нет */ }
  return (clientId = __JAMENDO_CLIENT_ID__ || "");
}

/**
 * Ошибка несёт КОД, а не готовый текст: сообщение собирается на языке
 * интерфейса. Раньше здесь была зашита русская строка, и она вылезала
 * посреди английской страницы.
 */
export type JamendoErrorCode = "no_key" | "empty" | "rate_limited" | "http" | "api";

export class JamendoError extends Error {
  constructor(readonly code: JamendoErrorCode, readonly detail = "") {
    super(code);
  }
}

/**
 * Jamendo регулярно отвечает пустой выдачей на совершенно нормальный запрос.
 *
 * Замер 2026-09-13: один и тот же URL, 20 раз с паузой 1.5 секунды — четыре
 * пустых ответа. Тридцать запросов подряд — одиннадцать пустых. Ни одного
 * кода 6 («Rate Limit Exceeded», он у них документирован), ни одного
 * заголовка о лимитах. Пустые ответы приходят БЫСТРЕЕ полных: 0.18 с против
 * 0.24 с и 105 байт против 19 КБ.
 *
 * То есть это не лимит и не наказание за частоту, а нестабильность выдачи:
 * часть запросов попадает на узел, который отвечает пустотой. Лечится
 * повтором, а не ожиданием — причём коротким, потому что следующая попытка
 * с той же вероятностью попадёт на исправный узел.
 *
 * Доля пустых при этом непостоянна: замер из браузера поймал окно, где пусто
 * было 20 раз из 20 подряд, а через минуту всё работало. Значит короткие
 * повторы вытягивают обычный разброс, а на плохое окно нужен выключатель.
 */
const RETRY_DELAYS = [250, 600, 1200];

/**
 * Выключатель на плохие окна: после трёх подряд неудачных подборов
 * перестаём спрашивать на минуту. Повторы в таком окне всё равно
 * бессмысленны, а очередь играет по кругу уже набранное.
 */
const BREAKER_AFTER = 3;
const BREAKER_MS = 60_000;
let consecutiveEmpty = 0;
let breakerUntil = 0;

export const breakerOpen = () => Date.now() < breakerUntil;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function call(params: Record<string, string>): Promise<Track[]> {
  const cid = await getClientId();
  if (!cid) throw new JamendoError("no_key");

  const qs = new URLSearchParams({ client_id: cid, format: "json", ...params });
  let lastCount = -1;

  for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
    const r = await fetch(`https://api.jamendo.com/v3.0/tracks/?${qs}`);
    if (!r.ok) throw new JamendoError("http", String(r.status));
    const d = await r.json();

    const h = d.headers ?? {};
    if (h.status !== "success") {
      // Код 6 — документированный «Rate Limit Exceeded». В отличие от пустой
      // выдачи это настоящий лимит, и вот его пережидать действительно надо.
      if (h.code === 6) throw new JamendoError("rate_limited", h.error_message);
      throw new JamendoError("api", h.error_message || String(h.code));
    }

    const results: Track[] = d.results ?? [];
    lastCount = results.length;
    if (results.length > 0) return results;

    const delay = RETRY_DELAYS[attempt];
    if (delay === undefined) break;
    await sleep(delay);
  }

  throw new JamendoError(lastCount === 0 ? "empty" : "http");
}

/**
 * Профиль → кандидаты, веером.
 *
 * Измерено 2026-09-13: `fuzzytags` НЕ смешивает теги, а даёт одному
 * подавить остальные — запрос `piano+metal` вернул 200 треков, из которых
 * metal все 200, а piano почти не представлен. То есть веса, посчитанные в
 * профиле, на стороне API пропадают.
 *
 * Поэтому спрашиваем по одному ведущему тегу на запрос и объединяем пулы,
 * а взвешенный выбор делаем у себя, где веса есть. На том же профиле:
 * один слитный запрос — 200 кандидатов, из них 76 совпали хотя бы по двум
 * терминам профиля; веер из трёх — 598 кандидатов и 238 совпавших.
 * Пулы почти не пересекаются, то есть слитный запрос видел лишь треть.
 */
export async function fetchCandidates(
  p: Profile,
  opts: { rotation?: number; drift?: number } = {},
): Promise<Track[]> {
  if (breakerOpen()) throw new JamendoError("empty");

  const queries = planQueries(p, { rotation: opts.rotation ?? 0, drift: opts.drift ?? 0, n: 3 });
  // Одинаковый профиль даёт одинаковую выдачу — переключение между городами
  // с похожей погодой не должно спрашивать Jamendo заново. В памяти, не на
  // диске: их условия запрещают проектировать приложение под кэш контента.
  //
  // В ключ входит и окно запроса: иначе следующая сессия достала бы из кэша
  // ровно то, ради ухода от чего окно и двигали.
  const shape = queries.map((q) => `${q.tag}@${q.offset}`).join("+");
  const key = `jam:${shape}|${queries[0]?.boost ?? ""}|${p.speed ?? ""}|${p.vocal ?? ""}|${p.acoustic ?? ""}`;
  const tracks = await cached(key, 15 * 60 * 1000, () => loadCandidates(p, queries), { persist: false })
    .catch((e) => {
      if (++consecutiveEmpty >= BREAKER_AFTER) breakerUntil = Date.now() + BREAKER_MS;
      throw e;
    });
  consecutiveEmpty = 0;
  breakerUntil = 0;
  return tracks;
}

async function loadCandidates(p: Profile, queries: Query[]): Promise<Track[]> {
  const base: Record<string, string> = {
    limit: "200",
    groupby: "artist_id",
    include: "musicinfo+stats",
    audioformat: "mp31",
    // По умолчанию обложка приходит 300x300, а рисуется на 184 CSS-пикселях —
    // на экране с двойной плотностью это растянутый источник. Замер по каталогу:
    // значения 200 и 400 игнорируются и всё равно дают 300, а 600 работает.
    imagesize: "600",
    durationbetween: `${p.duration[0]}_${p.duration[1]}`,
  };
  // Фильтровать по редкому темпу нельзя: `low` это 4.6% каталога, `verylow` 2% —
  // пул схлопывается, и все тихие дни начинают звучать одинаково (см. vocab.ts).
  if (p.speed && speedIsFilterable(p.speed)) base.speed = p.speed;
  if (p.vocal) base.vocalinstrumental = p.vocal;

  // Веер идёт по очереди с паузой, а не залпом: три одновременных запроса на
  // каждый подбор — это заметная нагрузка на бесплатный API, и именно она
  // быстрее всего приводит к троттлингу.
  const pools: Track[][] = [];
  for (const q of queries) {
    const params = { ...base, fuzzytags: q.tag, boost: q.boost, offset: String(q.offset) };
    try {
      pools.push(await call(params));
    } catch (e) {
      // Пустая выдача по одному тегу ничего не говорит про остальные — это
      // независимые попытки. А вот настоящий лимит распространяется на всё,
      // и тогда веер прекращаем.
      if (e instanceof JamendoError && e.code === "rate_limited") throw e;
      // Глубина каталога по тегу конечна: замер 2026-09-15 показал, что редкие
      // теги кончаются уже за offset 400. Пустота на сдвинутом окне — это не
      // «ничего нет», а «здесь уже край»; начало выдачи всегда полно.
      if (q.offset > 0) {
        try {
          pools.push(await call({ ...params, offset: "0" }));
          if (q !== queries[queries.length - 1]) await sleep(350);
          continue;
        } catch { /* и начало выдачи молчит — тег пропал целиком */ }
      }
      pools.push([]);
    }
    if (q !== queries[queries.length - 1]) await sleep(350);
  }

  const seen = new Set<string>();
  const merged: Track[] = [];
  for (const pool of pools) {
    for (const t of pool) {
      if (seen.has(t.id)) continue;
      seen.add(t.id);
      // Jamendo тоже отдаёт названия с HTML-сущностями: «Des K&ouml;nigs
      // Waldwanderung» приехало именно отсюда, а не из архива.
      merged.push({
        ...t,
        source: "jamendo",
        name: decodeEntities(t.name ?? ""),
        artist_name: decodeEntities(t.artist_name ?? ""),
        album_name: t.album_name ? decodeEntities(t.album_name) : t.album_name,
      });
    }
  }
  if (merged.length) return merged;

  const relaxed: Record<string, string> = {
    ...base,
    fuzzytags: queries.slice(0, 2).map((q) => q.tag).join("+"),
    boost: "popularity_month",
  };
  delete relaxed.speed;
  delete relaxed.durationbetween;
  delete relaxed.vocalinstrumental;
  return call(relaxed);
}

/**
 * Ссылка на страницу трека. Для Jamendo это обязательство clause 4.1 их
 * Terms of Use, для архива — требование лицензии CC (указание источника).
 */
export function trackUrl(t: Track): string {
  if (t.source === "archive") return t.shareurl ?? "https://archive.org/";
  return t.shareurl || `https://www.jamendo.com/track/${t.id}`;
}

export function artistUrl(t: Track): string {
  // У архива нет страниц артистов — ведём на саму запись.
  if (t.source === "archive") return t.shareurl ?? "https://archive.org/";
  return `https://www.jamendo.com/artist/${t.artist_id}`;
}
